import { useState, type FC } from 'react';
import { ChevronDown, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item';
import { cn } from '@/lib/utils';
import {
  getIcs234MatrixItemDraftName,
  Ics234MatrixInlineNameField,
  Ics234MatrixItemEditControls,
  type Ics234WorkAnalysisMatrixProps,
} from './Ics234MatrixShared';
import {
  MATRIX_ICON_BTN,
  MATRIX_KIND_BADGE,
  MATRIX_META_BADGE,
  MATRIX_OUTLINE_BTN,
  MATRIX_TRASH_BTN,
} from './matrixStyles';
import type {
  Ics234MatrixItemRef,
  Ics234ObjectiveRow,
  Ics234StrategyRow,
  Ics234TacticsRow,
} from './types';
import { ics234MatrixItemKey, ics234MatrixItemRefsMatch } from './types';

function strategyKey(objectiveId: string, strategyId: string) {
  return `${objectiveId}-${strategyId}`;
}

export const Ics234WorkAnalysisMatrixList: FC<
  Ics234WorkAnalysisMatrixProps
> = ({
  objectives,
  canEdit,
  isSaving,
  editingItem,
  onStartItemEdit,
  onCancelItemEdit,
  onPatchItemDraft,
  onSaveItem,
  onAddStrategy,
  onAddTactic,
  onDeleteObjective,
  onDeleteStrategy,
  onDeleteTactic,
}) => {
  const [expandedObjectiveId, setExpandedObjectiveId] = useState<string | null>(
    null
  );
  const [expandedStrategyKey, setExpandedStrategyKey] = useState<string | null>(
    null
  );

  const canMutate = canEdit;

  const isEditingRef = (ref: Ics234MatrixItemRef) =>
    !!editingItem && ics234MatrixItemRefsMatch(editingItem.ref, ref);

  const expandForRef = (ref: Ics234MatrixItemRef) => {
    if (ref.kind === 'objective') {
      setExpandedObjectiveId(ref.objectiveId);
    } else if (ref.kind === 'strategy') {
      setExpandedObjectiveId(ref.objectiveId);
      setExpandedStrategyKey(strategyKey(ref.objectiveId, ref.strategyId));
    } else {
      setExpandedObjectiveId(ref.objectiveId);
      setExpandedStrategyKey(strategyKey(ref.objectiveId, ref.strategyId));
    }
  };

  const startMatrixItemEdit = (ref: Ics234MatrixItemRef) => {
    onStartItemEdit(ref);
    expandForRef(ref);
  };

  // An item entering edit mode has to be visible, and the expansion has to
  // outlive the edit, so it is stored rather than derived. Adjusting during
  // render rather than from an effect avoids the second render pass a
  // synchronous setState inside an effect would cost.
  const editingKey = editingItem ? ics234MatrixItemKey(editingItem.ref) : null;
  const [lastEditingKey, setLastEditingKey] = useState(editingKey);
  if (editingKey !== lastEditingKey) {
    setLastEditingKey(editingKey);
    if (editingItem) {
      expandForRef(editingItem.ref);
    }
  }

  const renderTactic = (
    objective: Ics234ObjectiveRow,
    strategy: Ics234StrategyRow,
    tactic: Ics234TacticsRow,
    tacticIndex: number
  ) => {
    const ref: Ics234MatrixItemRef = {
      kind: 'tactic',
      objectiveId: objective.id,
      strategyId: strategy.id,
      tacticId: tactic.id,
    };
    const editing = isEditingRef(ref);
    const name = getIcs234MatrixItemDraftName(ref, tactic.name, editingItem);
    const placeholder = `Tactic ${tacticIndex + 1}`;

    return (
      <div
        key={tactic.id}
        className="tw:flex tw:items-start tw:gap-2 tw:rounded-md tw:border tw:border-neutral-600 tw:px-2 tw:py-1.5"
      >
        <div className="tw:min-w-0 tw:flex-1 tw:space-y-1.5">
          <Ics234MatrixInlineNameField
            name={name}
            editing={editing}
            placeholder={placeholder}
            onChange={(next) =>
              onPatchItemDraft({ kind: 'tactic', name: next })
            }
          />
          <Badge variant="outline" className={MATRIX_KIND_BADGE}>
            Tactic
          </Badge>
        </div>
        <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
          <Ics234MatrixItemEditControls
            itemRef={ref}
            itemLabel="tactic"
            canMutate={canMutate}
            isSaving={isSaving}
            editingItem={editingItem}
            onStartItemEdit={startMatrixItemEdit}
            onCancelItemEdit={onCancelItemEdit}
            onSaveItem={onSaveItem}
          />
          {canMutate && !editing ? (
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Delete tactic"
              className={MATRIX_TRASH_BTN}
              onClick={(event) => {
                event.stopPropagation();
                onDeleteTactic(objective.id, strategy.id, tactic.id);
              }}
            >
              <Trash2 className="tw:h-4 tw:w-4" />
            </Button>
          ) : null}
        </div>
      </div>
    );
  };

  const renderStrategy = (
    objective: Ics234ObjectiveRow,
    strategy: Ics234StrategyRow,
    strategyIndex: number
  ) => {
    const ref: Ics234MatrixItemRef = {
      kind: 'strategy',
      objectiveId: objective.id,
      strategyId: strategy.id,
    };
    const key = strategyKey(objective.id, strategy.id);
    const isOpen = expandedStrategyKey === key;
    const editing = isEditingRef(ref);
    const name = getIcs234MatrixItemDraftName(ref, strategy.name, editingItem);
    const placeholder = `Strategy ${strategyIndex + 1}`;
    const hasChildren = strategy.tactics.length > 0;

    return (
      <Collapsible
        key={strategy.id}
        open={isOpen}
        onOpenChange={(open) => setExpandedStrategyKey(open ? key : null)}
        className="tw:rounded-md tw:border tw:border-neutral-600"
      >
        <div className="tw:flex tw:items-start tw:gap-2 tw:px-2 tw:py-1.5">
          <div
            role="button"
            tabIndex={0}
            aria-expanded={isOpen}
            className="tw:min-w-0 tw:flex-1 tw:cursor-pointer tw:space-y-1.5"
            onClick={() =>
              setExpandedStrategyKey((previous) =>
                previous === key ? null : key
              )
            }
            onKeyDown={(event) => {
              // The inline name field lives inside this row, so only act on keys
              // aimed at the row itself - otherwise typing would collapse it.
              if (event.target !== event.currentTarget) return;
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              setExpandedStrategyKey((previous) =>
                previous === key ? null : key
              );
            }}
          >
            <Ics234MatrixInlineNameField
              name={name}
              editing={editing}
              placeholder={placeholder}
              onChange={(next) =>
                onPatchItemDraft({ kind: 'strategy', name: next })
              }
            />
            <Badge variant="outline" className={MATRIX_KIND_BADGE}>
              Strategy
            </Badge>
          </div>
          <div className="tw:flex tw:shrink-0 tw:items-center tw:gap-1">
            <Ics234MatrixItemEditControls
              itemRef={ref}
              itemLabel="strategy"
              canMutate={canMutate}
              isSaving={isSaving}
              editingItem={editingItem}
              onStartItemEdit={startMatrixItemEdit}
              onCancelItemEdit={onCancelItemEdit}
              onSaveItem={onSaveItem}
            />
            {canMutate && !editing ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Delete strategy"
                className={MATRIX_TRASH_BTN}
                disabled={hasChildren}
                onClick={(event) => {
                  event.stopPropagation();
                  onDeleteStrategy(objective.id, strategy.id);
                  if (expandedStrategyKey === key) {
                    setExpandedStrategyKey(null);
                  }
                  if (
                    editingItem &&
                    ics234MatrixItemKey(editingItem.ref).startsWith(
                      `str-${objective.id}-${strategy.id}`
                    )
                  ) {
                    onCancelItemEdit();
                  }
                }}
              >
                <Trash2 className="tw:h-4 tw:w-4" />
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Toggle strategy details"
              className={MATRIX_ICON_BTN}
              onClick={(event) => {
                event.stopPropagation();
                setExpandedStrategyKey((previous) =>
                  previous === key ? null : key
                );
              }}
            >
              <ChevronDown
                className={cn(
                  'tw:h-4 tw:w-4 tw:transition-transform',
                  isOpen && 'tw:rotate-180'
                )}
              />
            </Button>
          </div>
        </div>
        <CollapsibleContent>
          <div className="tw:space-y-2 tw:border-t tw:border-neutral-600 tw:px-2 tw:py-1.5">
            <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
              <p className="tw:text-sm tw:font-medium tw:text-white">
                Child Tactics
              </p>
              {canMutate && !editing ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className={MATRIX_OUTLINE_BTN}
                  onClick={() => {
                    onAddTactic(objective.id, strategy.id);
                    setExpandedObjectiveId(objective.id);
                    setExpandedStrategyKey(key);
                  }}
                >
                  + Add Tactic
                </Button>
              ) : null}
            </div>
            {strategy.tactics.length === 0 ? (
              <p className="tw:text-xs tw:text-neutral-400">
                No tactics recorded.
              </p>
            ) : (
              <div className="tw:space-y-2">
                {strategy.tactics.map((tactic, tacticIndex) =>
                  renderTactic(objective, strategy, tactic, tacticIndex)
                )}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    );
  };

  const renderObjectiveCard = (
    objective: Ics234ObjectiveRow,
    objectiveIndex: number
  ) => {
    const ref: Ics234MatrixItemRef = {
      kind: 'objective',
      objectiveId: objective.id,
    };
    const isOpen = expandedObjectiveId === objective.id;
    const editing = isEditingRef(ref);
    const name = getIcs234MatrixItemDraftName(ref, objective.name, editingItem);
    const placeholder = `Objective ${objectiveIndex + 1}`;
    const hasChildren = objective.strategies.length > 0;

    return (
      <Item
        key={objective.id}
        variant="outline"
        className="tw:flex-col tw:items-stretch tw:border-neutral-600 tw:bg-neutral-950 tw:p-0"
      >
        <Collapsible
          open={isOpen}
          onOpenChange={(open) =>
            setExpandedObjectiveId(open ? objective.id : null)
          }
        >
          <div className="tw:flex tw:items-start tw:gap-2 tw:px-3 tw:py-2.5">
            <ItemContent
              className="tw:min-w-0 tw:flex-1 tw:cursor-pointer tw:space-y-1.5"
              onClick={() =>
                setExpandedObjectiveId((previous) =>
                  previous === objective.id ? null : objective.id
                )
              }
            >
              <ItemTitle className="tw:line-clamp-none tw:w-full tw:font-medium tw:text-white">
                <Ics234MatrixInlineNameField
                  name={name}
                  editing={editing}
                  placeholder={placeholder}
                  onChange={(next) =>
                    onPatchItemDraft({ kind: 'objective', name: next })
                  }
                />
              </ItemTitle>
              <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
                <Badge variant="outline" className={MATRIX_KIND_BADGE}>
                  Objective
                </Badge>
                <Badge variant="secondary" className={MATRIX_META_BADGE}>
                  Operational
                </Badge>
              </div>
            </ItemContent>
            <ItemActions className="tw:shrink-0 tw:items-center tw:gap-1">
              <Ics234MatrixItemEditControls
                itemRef={ref}
                itemLabel="objective"
                canMutate={canMutate}
                isSaving={isSaving}
                editingItem={editingItem}
                onStartItemEdit={startMatrixItemEdit}
                onCancelItemEdit={onCancelItemEdit}
                onSaveItem={onSaveItem}
              />
              {canMutate && !editing ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Delete objective"
                  className={MATRIX_TRASH_BTN}
                  disabled={hasChildren}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDeleteObjective(objective.id);
                    if (expandedObjectiveId === objective.id) {
                      setExpandedObjectiveId(null);
                    }
                    if (
                      editingItem &&
                      (editingItem.ref.objectiveId === objective.id ||
                        ics234MatrixItemKey(editingItem.ref).startsWith(
                          `str-${objective.id}-`
                        ) ||
                        ics234MatrixItemKey(editingItem.ref).startsWith(
                          `tac-${objective.id}-`
                        ))
                    ) {
                      onCancelItemEdit();
                    }
                  }}
                >
                  <Trash2 className="tw:h-4 tw:w-4" />
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label="Toggle objective details"
                className={MATRIX_ICON_BTN}
                onClick={(event) => {
                  event.stopPropagation();
                  setExpandedObjectiveId((previous) =>
                    previous === objective.id ? null : objective.id
                  );
                }}
              >
                <ChevronDown
                  className={cn(
                    'tw:h-4 tw:w-4 tw:transition-transform',
                    isOpen && 'tw:rotate-180'
                  )}
                />
              </Button>
            </ItemActions>
          </div>
          <CollapsibleContent>
            <div className="tw:border-t tw:border-neutral-600 tw:px-3 tw:py-2 tw:text-sm">
              <div className="tw:rounded-md tw:p-2">
                <div className="tw:mb-2 tw:flex tw:items-center tw:justify-between tw:gap-2">
                  <p className="tw:text-sm tw:font-medium tw:text-white">
                    Child Strategies
                  </p>
                  {canMutate && !editing ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className={MATRIX_OUTLINE_BTN}
                      onClick={() => {
                        onAddStrategy(objective.id);
                        setExpandedObjectiveId(objective.id);
                      }}
                    >
                      + Add Strategy
                    </Button>
                  ) : null}
                </div>
                {objective.strategies.length === 0 ? (
                  <p className="tw:text-xs tw:text-neutral-400">
                    No strategies recorded.
                  </p>
                ) : (
                  <div className="tw:space-y-2">
                    {objective.strategies.map((strategy, strategyIndex) =>
                      renderStrategy(objective, strategy, strategyIndex)
                    )}
                  </div>
                )}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Item>
    );
  };

  if (objectives.length === 0) {
    return (
      <Item
        variant="outline"
        className="tw:border-neutral-600 tw:bg-neutral-950"
      >
        <ItemContent>
          <ItemTitle className="tw:text-white">
            No objectives recorded
          </ItemTitle>
          <ItemDescription className="tw:text-neutral-400">
            Add objectives to build the work analysis matrix.
          </ItemDescription>
        </ItemContent>
      </Item>
    );
  }

  return (
    <div className="tw:space-y-2">
      {objectives.map((objective, objectiveIndex) =>
        renderObjectiveCard(objective, objectiveIndex)
      )}
    </div>
  );
};
